import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { supabase } from '@/lib/supabase'
import { DealStatusBadge } from '../components/DealStatusBadge'
import type { DealStatus } from '../components/DealStatusBadge'

export const Route = createFileRoute('/')({
  component: IndexComponent,
})

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuctionRow {
  case_number: string
  auction_date: string
  judgment_amount: number | null
  sold_amount: number | null
  surplus: number | null
  property_address: string | null
  county: string | null
  reviewed_at: string | null
}

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

type SortCol = 'auction_date' | 'surplus'
type SortDir = 'asc' | 'desc'

const PAGE_SIZE = 50

function useAuctionData(
  sortCol: SortCol,
  sortDir: SortDir,
  searchTerm: string,
  hideDeficit: boolean,
  hideReviewed: boolean,
  page: number,
) {
  return useQuery<{ data: AuctionRow[]; count: number }>({
    queryKey: ['auctions', sortCol, sortDir, searchTerm, hideDeficit, hideReviewed, page],
    queryFn: async () => {
      let q = supabase
        .from('auctions')
        .select('case_number, auction_date, judgment_amount, sold_amount, surplus, property_address, county, reviewed_at', { count: 'exact' })

      if (hideDeficit) q = q.gte('surplus', 0)
      if (hideReviewed) q = q.is('reviewed_at', null)
      if (searchTerm) {
        const escaped = searchTerm.replace(/%/g, '\\%').replace(/_/g, '\\_')
        q = q.or(`case_number.ilike.%${escaped}%,property_address.ilike.%${escaped}%`)
      }

      q = q.order(sortCol, { ascending: sortDir === 'asc' })
      q = q.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      const { data, error, count } = await q
      if (error) throw new Error(error.message)
      return { data: (data ?? []) as AuctionRow[], count: count ?? 0 }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

function useDealsForCaseNumbers(caseNumbers: string[]) {
  return useQuery<Record<string, { id: string; status: DealStatus }>>({
    queryKey: ['deals-for-cases', caseNumbers],
    queryFn: async () => {
      if (caseNumbers.length === 0) return {}
      const { data, error } = await supabase
        .from('deals')
        .select('id, case_number, status')
        .in('case_number', caseNumbers)
      if (error) throw new Error(error.message)
      return Object.fromEntries(
        (data ?? []).map(d => [d.case_number, { id: d.id, status: d.status as DealStatus }])
      )
    },
    enabled: caseNumbers.length > 0,
    staleTime: 2 * 60 * 1000,
  })
}

function useDefenseAttorneyMap(caseNumbers: string[]) {
  return useQuery<Record<string, boolean>>({
    queryKey: ['defense-attorneys', caseNumbers],
    queryFn: async () => {
      if (caseNumbers.length === 0) return {}
      const { data, error } = await supabase
        .from('court_cases')
        .select('case_number, case_participants(role, lead_att_barnumber)')
        .in('case_number', caseNumbers)
      if (error) throw new Error(error.message)
      return Object.fromEntries(
        (data ?? []).map(cc => [
          cc.case_number,
          (cc.case_participants as { role: string; lead_att_barnumber: string | null }[])
            .some(cp => cp.role === 'defendant' && !!cp.lead_att_barnumber),
        ])
      )
    },
    enabled: caseNumbers.length > 0,
    staleTime: 10 * 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number | null): string {
  if (value == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-')
  return `${month}/${day}/${year}`
}

// ---------------------------------------------------------------------------
// Icons (inline SVG — no extra dep)
// ---------------------------------------------------------------------------

function ExternalLinkIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function FilterIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  )
}

function ScalesIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
      <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
      <path d="M7 21h10" />
      <path d="M12 3v18" />
      <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Filter modal
// ---------------------------------------------------------------------------

interface FilterModalProps {
  hideReviewed: boolean
  hideDeficit: boolean
  onChangeReviewed: (v: boolean) => void
  onChangeDeficit: (v: boolean) => void
  onClose: () => void
}

function FilterModal({ hideReviewed, hideDeficit, onChangeReviewed, onChangeDeficit, onClose }: FilterModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-end"
      onClick={onClose}
    >
      <div
        className="mt-16 mr-6 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-56"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-medium text-gray-700">Filters</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <label className="flex items-center gap-2 mb-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hideReviewed}
            onChange={e => onChangeReviewed(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm text-gray-700">Hide reviewed</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hideDeficit}
            onChange={e => onChangeDeficit(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm text-gray-700">Hide deficit</span>
        </label>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Dashboard table
// ---------------------------------------------------------------------------

interface AuctionTableProps {
  auctions: AuctionRow[]
  sortCol: SortCol
  sortDir: SortDir
  onSort: (col: SortCol) => void
  dealsMap: Record<string, { id: string; status: DealStatus }>
  hasAttorneyMap: Record<string, boolean>
  onMarkReviewed: (caseNumber: string) => void
  totalCount: number
  page: number
  onPageChange: (page: number) => void
}

function SortIndicator({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol; sortDir: SortDir }) {
  if (col !== sortCol) return <span className="ml-1 text-gray-300">↕</span>
  return <span className="ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span>
}

function AuctionTable({ auctions, sortCol, sortDir, onSort, dealsMap, hasAttorneyMap, onMarkReviewed, totalCount, page, onPageChange }: AuctionTableProps) {
  const navigate = useNavigate()

  const sortableHeader = (label: string, col: SortCol) => (
    <th
      key={col}
      onClick={() => onSort(col)}
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:text-gray-700 select-none"
    >
      {label}
      <SortIndicator col={col} sortCol={sortCol} sortDir={sortDir} />
    </th>
  )

  const staticHeader = (label: string) => (
    <th
      key={label}
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
    >
      {label}
    </th>
  )

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)
  const rangeStart = totalCount === 0 ? 0 : page * PAGE_SIZE + 1
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, totalCount)

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {staticHeader('Case Number')}
              {sortableHeader('Auction Date', 'auction_date')}
              {staticHeader('Judgment')}
              {staticHeader('Sold Amount')}
              {sortableHeader('Surplus', 'surplus')}
              {staticHeader('County')}
              {staticHeader('Atty')}
              {staticHeader('Deal')}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-16"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {auctions.map((row) => (
              <tr
                key={row.case_number}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate({ to: '/cases/$caseNumber', params: { caseNumber: row.case_number } })}
              >
                <td className="px-4 py-3 text-sm font-mono text-gray-900 whitespace-nowrap">{row.case_number}</td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(row.auction_date)}</td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatCurrency(row.judgment_amount)}</td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatCurrency(row.sold_amount)}</td>
                <td className="px-4 py-3 text-sm font-medium text-green-700 whitespace-nowrap">{formatCurrency(row.surplus)}</td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row.county ?? '—'}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {hasAttorneyMap[row.case_number]
                    ? <span title="Defendant has legal representation" className="text-amber-500"><ScalesIcon /></span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {dealsMap[row.case_number]
                    ? <DealStatusBadge status={dealsMap[row.case_number].status} />
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <a
                      href={`/cases/${row.case_number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in new tab"
                      className="text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <ExternalLinkIcon />
                    </a>
                    <button
                      title="Mark as reviewed"
                      onClick={() => onMarkReviewed(row.case_number)}
                      className="text-gray-400 hover:text-green-600 transition-colors"
                    >
                      <CheckIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center">
        <span className="text-sm text-gray-500">
          {totalCount === 0 ? '0 results' : `Showing ${rangeStart}–${rangeEnd} of ${totalCount}`}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>
          <span className="text-sm text-gray-500">
            {page + 1} / {Math.max(totalPages, 1)}
          </span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
            className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function IndexComponent() {
  const { user, isLoading, logout } = useAuth()
  const navigate = useNavigate()

  const [sortCol, setSortCol] = useState<SortCol>('auction_date')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [searchRaw, setSearchRaw] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [hideDeficit, setHideDeficit] = useState(true)
  const [hideReviewed, setHideReviewed] = useState(true)
  const [filterOpen, setFilterOpen] = useState(false)
  const [page, setPage] = useState(0)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchRaw.trim()), 400)
    return () => clearTimeout(id)
  }, [searchRaw])

  // Reset to page 0 whenever filters, search, or sort change
  useEffect(() => {
    setPage(0)
  }, [debouncedSearch, sortCol, sortDir, hideDeficit, hideReviewed])

  function handleSort(col: SortCol) {
    if (col === sortCol) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortCol(col)
      setSortDir('desc')
    }
  }

  const filtersOverridden = !hideReviewed || !hideDeficit
  const queryClient = useQueryClient()

  const { data: auctionResult, isLoading: dataLoading, error: dataError } = useAuctionData(
    sortCol, sortDir, debouncedSearch, hideDeficit, hideReviewed, page
  )
  const auctions = auctionResult?.data ?? []
  const totalCount = auctionResult?.count ?? 0

  const caseNumbers = auctions.map(a => a.case_number)
  const { data: dealsMap = {} } = useDealsForCaseNumbers(caseNumbers)
  const { data: hasAttorneyMap = {} } = useDefenseAttorneyMap(caseNumbers)

  async function handleMarkReviewed(caseNumber: string) {
    const queryKey = ['auctions', sortCol, sortDir, debouncedSearch, hideDeficit, hideReviewed, page]
    queryClient.setQueryData(queryKey, (old: { data: AuctionRow[]; count: number } | undefined) => ({
      data: old?.data.filter(a => a.case_number !== caseNumber) ?? [],
      count: (old?.count ?? 1) - 1,
    }))
    if (auctions.length === 1 && page > 0) {
      setPage(p => p - 1)
    }
    const { error } = await supabase
      .from('auctions')
      .update({ reviewed_at: new Date().toISOString() })
      .eq('case_number', caseNumber)
    if (error) {
      queryClient.invalidateQueries({ queryKey: ['auctions'] })
    }
  }

  useEffect(() => {
    if (!isLoading && !user) {
      navigate({ to: '/login' })
    }
  }, [user, isLoading, navigate])

  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-gray-900">Auction Surplus Cases - 3rd Party Bidder</h2>
        <div className="flex gap-3 items-center">
          <span className="text-sm text-gray-500">{user.email}</span>
          <button
            onClick={logout}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Sign Out
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={searchRaw}
          onChange={e => setSearchRaw(e.target.value)}
          placeholder="Search by case number or address…"
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          onClick={() => setFilterOpen(o => !o)}
          title="Filters"
          className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md transition-colors ${
            filtersOverridden
              ? 'border-blue-400 text-blue-600 bg-blue-50 hover:bg-blue-100'
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          }`}
        >
          <FilterIcon />
          Filters
          {filtersOverridden && (
            <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
          )}
        </button>
      </div>

      {filterOpen && (
        <FilterModal
          hideReviewed={hideReviewed}
          hideDeficit={hideDeficit}
          onChangeReviewed={setHideReviewed}
          onChangeDeficit={setHideDeficit}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {dataLoading && (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-gray-500">Loading auction data...</p>
        </div>
      )}

      {dataError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">Error loading data: {(dataError as Error).message}</p>
        </div>
      )}

      {!dataLoading && !dataError && totalCount === 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-gray-500">No surplus cases to display. Run the scraper to fetch data.</p>
        </div>
      )}

      {!dataLoading && !dataError && totalCount > 0 && (
        <AuctionTable
          auctions={auctions}
          sortCol={sortCol}
          sortDir={sortDir}
          onSort={handleSort}
          dealsMap={dealsMap}
          hasAttorneyMap={hasAttorneyMap}
          onMarkReviewed={handleMarkReviewed}
          totalCount={totalCount}
          page={page}
          onPageChange={setPage}
        />
      )}
    </div>
  )
}
